import { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, {
  Background, Controls, MiniMap, applyNodeChanges, applyEdgeChanges, addEdge,
} from "reactflow";
import "reactflow/dist/style.css";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Zap, Server, Database, HardDrive, Users, Cog } from "lucide-react";

const KIND_STYLES = {
  front: { color: "#00E5FF", icon: Zap, label: "Frontend" },
  api: { color: "#00FF88", icon: Cog, label: "API" },
  db: { color: "#FF9900", icon: Database, label: "Database" },
  storage: { color: "#8B5CF6", icon: HardDrive, label: "Storage" },
  partner: { color: "#FF3B30", icon: Users, label: "Partner" },
  service: { color: "#0EA5E9", icon: Server, label: "Service" },
};

function SegmentNode({ data }) {
  const style = KIND_STYLES[data.kind] || KIND_STYLES.service;
  const Icon = style.icon;
  return (
    <div
      className="rounded-sm border bg-black/80 backdrop-blur px-3 py-2 min-w-[160px]"
      style={{ borderColor: style.color + "60", boxShadow: `0 0 12px ${style.color}30` }}
      data-testid={`segment-node-${data.id}`}
    >
      <div className="flex items-center gap-2 text-white">
        <Icon className="h-4 w-4" style={{ color: style.color }} />
        <div className="text-sm font-semibold">{data.name}</div>
      </div>
      <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
        {style.label} · {data.site}
      </div>
    </div>
  );
}

const nodeTypes = { segment: SegmentNode };

export default function SegmentsPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [segments, setSegments] = useState([]);
  const [rules, setRules] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selected, setSelected] = useState({ source: null, target: null });
  const [lastFlow, setLastFlow] = useState(null);

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([api.get("/segments"), api.get("/segments/rules")]);
    setSegments(s.data); setRules(r.data);
    setNodes(s.data.map((seg) => ({
      id: seg._id, type: "segment",
      position: { x: seg.x || 0, y: seg.y || 0 },
      data: { name: seg.name, kind: seg.kind, site: seg.site, id: seg._id },
    })));
    setEdges(r.data.map((rule) => ({
      id: rule._id,
      source: rule.source_id, target: rule.target_id,
      animated: rule.action === "allow",
      style: {
        stroke: rule.action === "allow" ? "#00FF88" : "#FF3B30",
        strokeWidth: 2, strokeDasharray: rule.action === "deny" ? "6 4" : undefined,
      },
      label: rule.action.toUpperCase(),
      labelStyle: { fill: rule.action === "allow" ? "#00FF88" : "#FF3B30", fontFamily: "monospace", fontSize: 10 },
    })));
  }, []);

  useEffect(() => { load(); }, [load, user?.mode]);

  const onNodesChange = useCallback((c) => setNodes((n) => applyNodeChanges(c, n)), []);
  const onEdgesChange = useCallback((c) => setEdges((e) => applyEdgeChanges(c, e)), []);

  const simulateFlow = async () => {
    if (!selected.source || !selected.target) {
      toast.error("Sélectionnez un segment source et une cible");
      return;
    }
    try {
      const { data } = await api.post("/segments/simulate-flow", {
        source_id: selected.source, target_id: selected.target,
      });
      setLastFlow(data);
      toast[data.action === "allow" ? "success" : "error"](`${data.flow} → ${data.action.toUpperCase()}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="segments-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
            {t("segments")}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Cartographie interactive</h1>
        </div>
      </div>

      <Card className="bg-white/[0.02] border-white/10 rounded-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SegmentSelect label="Segment source" segments={segments}
            value={selected.source} onChange={(v) => setSelected({ ...selected, source: v })}
            testId="src-select" />
          <SegmentSelect label="Segment cible" segments={segments}
            value={selected.target} onChange={(v) => setSelected({ ...selected, target: v })}
            testId="tgt-select" />
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <Button onClick={simulateFlow} data-testid="simulate-flow-btn"
                  className="bg-cyan-500 text-black hover:bg-cyan-400">
            Simuler flux est-ouest
          </Button>
          {lastFlow && (
            <div className="flex items-center gap-2 text-sm">
              <span className="font-mono text-white/70">{lastFlow.flow}</span>
              <StatusBadge status={lastFlow.action === "allow" ? "trusted" : "blocked"}>
                {lastFlow.action}
              </StatusBadge>
            </div>
          )}
        </div>
      </Card>

      <Card className="bg-white/[0.02] border-white/10 rounded-sm overflow-hidden"
            style={{ height: 520 }}>
        <ReactFlow
          nodes={nodes} edges={edges}
          onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes} fitView proOptions={{ hideAttribution: true }}
        >
          <Background color="#ffffff10" gap={20} />
          <Controls className="!bg-black/60 !border !border-white/10" />
          <MiniMap className="!bg-black/60 !border !border-white/10" nodeColor={(n) => KIND_STYLES[n.data?.kind]?.color || "#666"} />
        </ReactFlow>
      </Card>

      <Card className="bg-white/[0.02] border-white/10 rounded-sm p-4">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-3">
          Règles est-ouest ({rules.length})
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 font-mono text-xs">
          {rules.map((r) => {
            const src = segments.find((s) => s._id === r.source_id);
            const tgt = segments.find((s) => s._id === r.target_id);
            return (
              <div key={r._id} className="flex items-center justify-between border border-white/10 rounded-sm p-2">
                <span className="text-white/80">
                  {src?.name || "?"} → {tgt?.name || "?"}
                </span>
                <StatusBadge status={r.action === "allow" ? "trusted" : "blocked"}>
                  {r.action}
                </StatusBadge>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function SegmentSelect({ label, segments, value, onChange, testId }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">{label}</div>
      <select
        data-testid={testId}
        className="w-full bg-white/[0.03] border border-white/10 rounded-sm px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-cyan-500/60"
        value={value || ""} onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— sélectionner —</option>
        {segments.map((s) => (
          <option key={s._id} value={s._id}>{s.name}</option>
        ))}
      </select>
    </div>
  );
}
